import { SUPPORTED_PROVIDERS } from 'myagent_core'
import { useEffect, useState } from 'react'
import './SettingsModal.css'

const DEFAULT_PROVIDER = SUPPORTED_PROVIDERS[0].id
const CUSTOM_MODEL_SENTINEL = '__custom__'

const findProvider = (id: string) => SUPPORTED_PROVIDERS.find(p => p.id === id)

const isPresetModel = (providerId: string, model: string): boolean => {
  const p = findProvider(providerId)
  return !!p && p.models.includes(model)
}

const initialModelFor = (providerId: string, savedModel: string | undefined): string => {
  if (savedModel) return savedModel
  return findProvider(providerId)?.defaultModel || ''
}

export const LlmBotEditor = ({ bot, onSave, onCancel, onDelete, onStartChat }: any) => {
  const isNew = !bot
  const [displayName, setDisplayName] = useState('')
  const [description, setDescription] = useState('')
  const [provider, setProvider] = useState(DEFAULT_PROVIDER)
  const [model, setModel] = useState(() => initialModelFor(DEFAULT_PROVIDER, undefined))
  const [isCustomModel, setIsCustomModel] = useState(false)
  const [apiKey, setApiKey] = useState('')
  const [systemPrompt, setSystemPrompt] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (bot) {
      const nextProvider = bot.llm?.provider || DEFAULT_PROVIDER
      const nextModel = initialModelFor(nextProvider, bot.llm?.model)
      setDisplayName(bot.displayName || '')
      setDescription(bot.description || '')
      setProvider(nextProvider)
      setModel(nextModel)
      setIsCustomModel(!!nextModel && !isPresetModel(nextProvider, nextModel))
      setApiKey(bot.llm?.apiKey || '')
      setSystemPrompt(bot.role?.systemPrompt || '')
    } else {
      setDisplayName('')
      setDescription('')
      setProvider(DEFAULT_PROVIDER)
      setModel(initialModelFor(DEFAULT_PROVIDER, undefined))
      setIsCustomModel(false)
      setApiKey('')
      setSystemPrompt('')
    }
  }, [bot])

  const handleProviderChange = (nextProvider: string) => {
    setProvider(nextProvider)
    const nextDefault = findProvider(nextProvider)?.defaultModel || ''
    setModel(nextDefault)
    setIsCustomModel(false)
  }

  const handleModelSelectChange = (value: string) => {
    if (value === CUSTOM_MODEL_SENTINEL) {
      setIsCustomModel(true)
      setModel('')
    } else {
      setIsCustomModel(false)
      setModel(value)
    }
  }

  const handleSave = async () => {
    if (!displayName.trim()) {
      setError('Name is required')
      return
    }
    setIsSaving(true)
    setError('')
    try {
      await onSave({
        ...(bot ? { botId: bot.botId } : {}),
        displayName: displayName.trim(),
        description: description.trim(),
        llm: {
          provider: provider || null,
          model: model || null,
          apiKey: apiKey || null,
          workerPublicKey: null,
        },
        role: {
          systemPrompt: systemPrompt.trim() || null,
        },
      })
    } catch (err: any) {
      setError(err.message || 'Failed to save')
    } finally {
      setIsSaving(false)
    }
  }

  const currentProvider = findProvider(provider)
  const presetModels = currentProvider?.models ?? []
  const selectValue = isCustomModel ? CUSTOM_MODEL_SENTINEL : model

  return (
    <div className="settings-panel" data-testid="bot-editor">
      <div className="settings-content">
        <div className="settings-section">
          <h3>{isNew ? 'New Bot' : 'Edit Bot'}</h3>
          <div className="bot-config-field">
            <label className="bot-config-label">Name</label>
            <input
              className="provider-input"
              value={displayName}
              onChange={e => setDisplayName(e.target.value)}
              placeholder="Code Reviewer"
            />
          </div>
          <div className="bot-config-field" style={{ marginTop: 12 }}>
            <label className="bot-config-label">Description</label>
            <input
              className="provider-input"
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Brief description"
            />
          </div>
        </div>

        <div className="settings-section">
          <h3>LLM</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div className="bot-config-field">
              <label className="bot-config-label">Provider</label>
              <select
                className="provider-input"
                value={provider}
                onChange={e => handleProviderChange(e.target.value)}
              >
                {SUPPORTED_PROVIDERS.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.displayName}
                  </option>
                ))}
              </select>
            </div>
            <div className="bot-config-field">
              <div className="field-label-row">
                <label className="bot-config-label">API Key</label>
                {currentProvider && (
                  <a
                    className="field-hint-link"
                    href={currentProvider.apiKeyUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={`Get your ${currentProvider.displayName} API key`}
                  >
                    Get key ↗
                  </a>
                )}
              </div>
              <input
                className="provider-input"
                type="password"
                value={apiKey}
                onChange={e => setApiKey(e.target.value)}
                placeholder={currentProvider?.apiKeyPlaceholder ?? 'sk-...'}
              />
            </div>
            <div className="bot-config-field">
              <label className="bot-config-label">Model</label>
              <select
                className="provider-input"
                value={selectValue}
                onChange={e => handleModelSelectChange(e.target.value)}
              >
                {presetModels.map(m => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
                <option value={CUSTOM_MODEL_SENTINEL}>Custom...</option>
              </select>
              {isCustomModel && (
                <input
                  className="provider-input"
                  value={model}
                  onChange={e => setModel(e.target.value)}
                  placeholder="custom-model-id"
                  style={{ marginTop: 8 }}
                />
              )}
            </div>
          </div>
        </div>

        <div className="settings-section">
          <h3>System Prompt</h3>
          <textarea
            className="provider-input bot-config-textarea"
            value={systemPrompt}
            onChange={e => setSystemPrompt(e.target.value)}
            placeholder="You are a helpful assistant specialized in..."
            rows={5}
            style={{ resize: 'vertical', minHeight: 80, fontFamily: 'inherit', lineHeight: 1.5 }}
          />
        </div>

        {error && <div className="settings-error">{error}</div>}
      </div>

      <div className="settings-footer">
        {!isNew && onDelete && (
          <button
            className="cancel-button"
            style={{
              color: 'var(--color-error)',
              borderColor: 'var(--color-error)',
              marginRight: 'auto',
            }}
            onClick={() => onDelete(bot.botId)}
          >
            Delete
          </button>
        )}
        {!isNew && onStartChat && (
          <button
            className="save-button"
            style={{ background: 'var(--color-primary)' }}
            onClick={() => onStartChat(bot)}
          >
            Chat
          </button>
        )}
        <button className="cancel-button" onClick={onCancel}>
          Cancel
        </button>
        <button className="save-button" onClick={handleSave} disabled={isSaving}>
          {isSaving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>
  )
}
